/**
 * PWA Update Hook
 * Wraps useRegisterSW from vite-plugin-pwa to handle service worker updates
 *
 * Features:
 * - Detects when a new service worker version is available
 * - Provides function to trigger update and reload
 * - Sets up periodic background checks for updates (hourly)
 */

import { useRegisterSW } from 'virtual:pwa-register/react'
import { useCallback, useEffect, useRef } from 'react'

// Check for updates every hour (in milliseconds)
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000

export interface UsePWAUpdateReturn {
    /**
     * Whether a new version is available and waiting to be installed
     */
    needRefresh: boolean

    /**
     * Whether the app is ready to work offline
     */
    offlineReady: boolean

    /**
     * Trigger the service worker update and reload the page
     */
    updateAndReload: () => Promise<void>

    /**
     * Dismiss the update notification (sets needRefresh to false)
     * Note: The update will still be applied on next page load
     */
    dismissUpdate: () => void
}

/**
 * Hook for managing PWA service worker updates
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { needRefresh, updateAndReload } = usePWAUpdate()
 *
 *   useEffect(() => {
 *     if (needRefresh) {
 *       // Auto-reload when update is available
 *       updateAndReload()
 *     }
 *   }, [needRefresh, updateAndReload])
 *
 *   return null
 * }
 * ```
 */
export function usePWAUpdate(): UsePWAUpdateReturn {
    const registrationRef = useRef<ServiceWorkerRegistration | null>(null)
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const {
        needRefresh: [needRefresh, setNeedRefresh],
        offlineReady: [offlineReady, setOfflineReady],
        updateServiceWorker,
    } = useRegisterSW({
        onRegisteredSW(swScriptUrl, registration) {
            console.log('[PWA] Service worker registered:', swScriptUrl)

            if (registration) {
                registrationRef.current = registration

                // Clear any existing interval before setting a new one
                if (intervalRef.current) {
                    clearInterval(intervalRef.current)
                }

                // Set up periodic update checks
                intervalRef.current = setInterval(() => {
                    console.log('[PWA] Checking for updates...')
                    registration.update().catch((error) => {
                        console.warn('[PWA] Update check failed:', error)
                    })
                }, UPDATE_CHECK_INTERVAL)
            }
        },
        onRegisterError(error) {
            console.error('[PWA] Service worker registration error:', error)
        },
        onNeedRefresh() {
            console.log('[PWA] New content available')
        },
        onOfflineReady() {
            console.log('[PWA] App ready to work offline')
        },
    })

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [])

    // Trigger update and reload the page
    const updateAndReload = useCallback(async () => {
        console.log('[PWA] Triggering update and reload...')
        await updateServiceWorker(true) // true = reload page after activation
    }, [updateServiceWorker])

    // Dismiss the update notification
    const dismissUpdate = useCallback(() => {
        setNeedRefresh(false)
        setOfflineReady(false)
    }, [setNeedRefresh, setOfflineReady])

    // Log state changes for debugging
    useEffect(() => {
        if (needRefresh) {
            console.log('[PWA] needRefresh state changed to true')
        }
    }, [needRefresh])

    return {
        needRefresh,
        offlineReady,
        updateAndReload,
        dismissUpdate,
    }
}

export default usePWAUpdate
